/**
 * The single source of truth for navigation.
 *
 * Layout.tsx used to carry two hand-maintained copies of the menu — one for the
 * desktop bar, one for the mobile drawer — and they drifted. `/admin/audit-log`
 * was added to the desktop dropdown and never to the drawer, so Audit Logs was
 * simply unreachable on a phone; `/profile` and `/licenses/ownership-changes`
 * had the same problem. Rendering both from these arrays makes that class of
 * bug impossible: a new entry appears everywhere or nowhere.
 *
 * `e2e/specs/20-nav-coverage.spec.ts` asserts the two rendered menus expose an
 * identical set of links, so a regression fails a test rather than surfacing in
 * the field.
 *
 * ## Grouping is an authorization statement
 *
 * The menu used to say "Settings" over a block that contained Ownership
 * Changes, Do Not Contact and the Audit Log — none of which are settings — and
 * the whole block was gated with a single `{isAdmin && …}` in the renderer.
 * That worked only because every item in it happened to be admin-only.
 *
 * Groups are now honest about what they hold, which means a group can be
 * **mixed** (Tools has one item every user may see and two that only an admin
 * may), so visibility has to be decided **per item**. `adminOnly` is therefore
 * required on every `NavItem`: forgetting it is a compile error, and
 * `visibleTo` tests for `=== false` so that a flag which somehow arrives
 * `undefined` at runtime is *hidden* rather than exposed. Default deny.
 *
 * The flag is UX only. Every admin route is independently enforced by
 * `AdminRoute` in `App.tsx` and again by the API; hiding a link is about not
 * advertising a page that would immediately bounce you.
 */
import {
  AntennaIcon,
  AuditIcon,
  BellIcon,
  ClientsIcon,
  DatabaseIcon,
  DeadlineIcon,
  EnvelopeIcon,
  ExportIcon,
  GearIcon,
  IdCardIcon,
  ImportIcon,
  InfoIcon,
  LeadsIcon,
  NoContactIcon,
  OwnershipIcon,
  SearchIcon,
  ShieldIcon,
  TasksIcon,
  ToolsIcon,
  UserCircleIcon,
  UserGroupIcon,
  type NavIcon,
} from './navIcons'

/**
 * Named badges. `alerts` (red) counts open critical/high security alerts;
 * `audit` (red) counts security events that went wrong;
 * `construction` (amber) counts client/lead construction deadlines due soon that
 * nobody has contacted yet: a to-do count, deliberately not an alarm colour.
 */
export type NavBadge = 'alerts' | 'audit' | 'construction'

export interface NavItem {
  to: string
  label: string
  /**
   * Required on purpose — see the file header. `true` renders only for
   * superusers; `false` is an explicit statement that the page is open to
   * every signed-in user.
   */
  adminOnly: boolean
  /**
   * Additionally requires the employee (or admin) role — for a page a *viewer*
   * must not see. Only meaningful alongside `adminOnly: false`; an item without
   * it is visible to every signed-in user, as before.
   */
  employeeOrAdmin?: boolean
  /** Referenced by value, so an item with no icon does not compile. */
  icon: NavIcon
  /** Named badge to render alongside the label. */
  badge?: NavBadge
}

export interface NavGroup {
  /**
   * The dropdown panel's DOM id, and the `aria-controls` value on its trigger.
   * `e2e/specs/20-nav-coverage.spec.ts` expands dropdowns by this id — a new
   * group must be added to that spec's list in the same commit, or every link
   * inside it reads as missing from the desktop menu.
   */
  id: string
  label: string
  icon: NavIcon
  items: NavItem[]
}

/**
 * Top-level links: the desktop bar, and the first block of the drawer.
 *
 * Deliberately four. The bar needs ~1000px for its links plus the dropdowns,
 * the user's name and Logout (see the Layout.tsx header) — Construction moved
 * into Tools partly because it belongs there and partly because the bar had no
 * room to grow.
 */
export const NAV_PRIMARY: NavItem[] = [
  { to: '/search', label: 'Search', adminOnly: false, icon: SearchIcon },
  { to: '/crm/leads', label: 'Leads', adminOnly: false, icon: LeadsIcon },
  { to: '/crm/clients', label: 'Clients', adminOnly: false, icon: ClientsIcon },
  { to: '/crm/tasks', label: 'Tasks', adminOnly: false, icon: TasksIcon },
]

/**
 * Working views over the license data — things you *look at*, not things you
 * configure. Mixed access: Construction Deadlines is open to everyone, the
 * other two are `AdminRoute`-guarded in App.tsx (and Ownership Changes is
 * admin-only despite not living under `/admin/`).
 */
const NAV_TOOLS: NavGroup = {
  id: 'tools-menu',
  label: 'Tools',
  icon: ToolsIcon,
  items: [
    {
      to: '/construction-deadlines',
      label: 'Construction Deadlines',
      adminOnly: false,
      icon: DeadlineIcon,
      badge: 'construction',
    },
    {
      to: '/licenses/ownership-changes',
      label: 'Ownership Changes',
      adminOnly: true,
      icon: OwnershipIcon,
    },
    // Frequency coordination, search 3a. Admin-only because the feature is IN
    // DEVELOPMENT and its answer must not be relied on, not because the data is
    // sensitive — FCC assignments are public. There is no feature-flag mechanism in
    // this codebase (the key/value app_config table was deliberately dropped), and in a
    // shop of under five people the superusers ARE the tester set. If that changes, a
    // second axis belongs on `visibleTo` below, not on a new settings table.
    {
      to: '/coordination',
      label: 'Coordination (beta)',
      adminOnly: true,
      icon: AntennaIcon,
    },
    // Employees may read the list and record WHY somebody is on it (2026-09-21).
  // Creating, deleting and bulk-removing entries remain admin-only, enforced by the
  // API and reflected in the page's own controls — see DoNotContactPage.
  { to: '/admin/do-not-contact', label: 'Do Not Contact', adminOnly: false, employeeOrAdmin: true, icon: NoContactIcon },
  ],
}

/**
 * Everything that requires a superuser: the genuine settings, plus the two
 * admin capabilities that are not settings at all (Users, Audit Log).
 */
const NAV_ADMIN: NavGroup = {
  id: 'admin-menu',
  label: 'Admin',
  icon: ShieldIcon,
  items: [
    { to: '/admin/settings', label: 'General Settings', adminOnly: true, icon: GearIcon },
    /*
     * One entry, four tabs. This used to be four separate items — Notifications,
     * SMTP Settings, Email Templates and Business Cards — which is most of why
     * the owner called this part of the app clunky: nothing said those four
     * pages were the same subject. The old paths still resolve; App.tsx
     * redirects each to its tab.
     */
    { to: '/admin/email', label: 'Email', adminOnly: true, icon: EnvelopeIcon },
    { to: '/admin/import', label: 'FCC Import', adminOnly: true, icon: ImportIcon },
    { to: '/admin/crm-export', label: 'CRM Export', adminOnly: true, icon: ExportIcon },
    { to: '/admin/backup', label: 'Backup & Restore', adminOnly: true, icon: DatabaseIcon },
    { to: '/admin/users', label: 'Users', adminOnly: true, icon: UserGroupIcon },
    { to: '/admin/security-alerts', label: 'Security Alerts', adminOnly: true, icon: BellIcon, badge: 'alerts' },
    { to: '/admin/audit-log', label: 'Audit Log', adminOnly: true, icon: AuditIcon, badge: 'audit' },
  ],
}

/** Rendered as dropdowns on desktop, as labelled blocks in the drawer. */
export const NAV_GROUPS: NavGroup[] = [NAV_TOOLS, NAV_ADMIN]

/** Personal links: the desktop user dropdown, and a drawer block. */
export const NAV_USER: NavItem[] = [
  { to: '/profile', label: 'My Profile', adminOnly: false, icon: UserCircleIcon },
  { to: '/profile/business-cards', label: 'My Cards', adminOnly: false, icon: IdCardIcon },
]

/**
 * About sits on its own: it is not admin-only, but it used to be rendered
 * inside the admin Settings dropdown, which meant a non-admin had no way to
 * reach it on desktop.
 */
export const NAV_ABOUT: NavItem[] = [
  { to: '/about', label: 'About', adminOnly: false, icon: InfoIcon },
]

/**
 * The one place that decides whether a nav entry is rendered.
 *
 * Fail-closed by construction: only an explicit `adminOnly: false` is shown to
 * a non-admin, so a malformed entry disappears instead of leaking.
 */
export const visibleTo = (
  items: NavItem[], isAdmin: boolean, isEmployee = false,
): NavItem[] =>
  items.filter((i) => {
    if (isAdmin) return true
    // Default deny is unchanged: only an explicit `adminOnly: false` is ever shown.
    if (i.adminOnly !== false) return false
    // A second, narrower gate for pages a viewer must not see. Still fail-closed:
    // the extra requirement can only ever REMOVE an item.
    if (i.employeeOrAdmin === true) return isEmployee
    return true
  })

/** A group is rendered only when it has something in it for this user. */
export const visibleGroups = (
  groups: NavGroup[], isAdmin: boolean, isEmployee = false,
): NavGroup[] =>
  groups.filter((g) => visibleTo(g.items, isAdmin, isEmployee).length > 0)
